import React from 'react';
import { Helmet } from 'react-helmet-async';
import { resolveBackendImage } from '../utils/brandLogo';

const SITE_NAME = 'Empreende Comunidade';
const DEFAULT_DESCRIPTION = 'Encontre os melhores negócios locais da sua região. Conecte-se com empreendedores, descubra serviços, produtos e promoções perto de você.';
// OG image: /og-image.png 1200×630 (gerado p/ social sharing).
const DEFAULT_IMAGE = '/og-image.png';

/**
 * SEOHead - Reusable SEO component for all public pages.
 * Renders title, meta description, canonical, Open Graph, Twitter Card e JSON-LD.
 *
 * @param {string} title - Page title (appended with site name)
 * @param {string} description - Meta description
 * @param {string} canonical - Canonical URL path (e.g. /mg/ponte-nova/alimentos)
 * @param {string} image - OG image URL
 * @param {string} type - OG type (website, article, place)
 * @param {boolean} noindex - If true, adds noindex
 * @param {object|array} jsonLd - JSON-LD structured data
 * @param {string} locale - Content locale
 * @param {string} preloadImage - URL da imagem crítica acima da dobra (LCP).
 *   Quando informada, dispara `<link rel="preload" as="image" fetchpriority="high">`
 *   para que o navegador inicie o download em paralelo com o JS bundle.
 *   Pode reduzir LCP em 200–400ms em páginas individuais (negócio/notícia/evento).
 */
export default function SEOHead({
  title,
  description = DEFAULT_DESCRIPTION,
  canonical,
  image = DEFAULT_IMAGE,
  type = 'website',
  noindex = false,
  jsonLd,
  locale = 'pt_BR',
  preloadImage,
}) {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : SITE_NAME;
  const baseUrl = window.location.origin;
  const canonicalUrl = canonical ? `${baseUrl}${canonical}` : window.location.href.split('?')[0];

  // Resolve preload image (relativa /api/uploads/... → host atual)
  const preloadHref = preloadImage ? resolveBackendImage(preloadImage) : null;

  return (
    <Helmet>
      <html lang="pt-BR" />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />

      {noindex && <meta name="robots" content="noindex, nofollow" />}
      {!noindex && <meta name="robots" content="index, follow" />}

      {/* Preload do hero image — diminui LCP. fetchpriority=high prioriza no
          waterfall do browser. Só renderiza se o caller informou. */}
      {preloadHref && (
        <link rel="preload" as="image" href={preloadHref} fetchpriority="high" />
      )}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content={locale} />
      {image && <meta property="og:image" content={image.startsWith('http') ? image : `${baseUrl}${image}`} />}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      {image && <meta name="twitter:image" content={image.startsWith('http') ? image : `${baseUrl}${image}`} />}

      {/* JSON-LD */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(Array.isArray(jsonLd) ? jsonLd : jsonLd)}
        </script>
      )}
    </Helmet>
  );
}

/**
 * Build LocalBusiness JSON-LD for a business page
 */
export function buildLocalBusinessJsonLd(business, municipio) {
  const baseUrl = window.location.origin;
  const slug = business.slug || business.id;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: business.nome,
    url: `${baseUrl}/negocio/${slug}--${business.id}`,
  };

  if (business.descricao) schema.description = business.descricao;
  if (business.avatar_url) schema.image = business.avatar_url;
  if (business.telefone) schema.telephone = business.telefone;
  if (business.email) schema.email = business.email;
  if (business.site) schema.url = business.site;

  // Address
  if (business.endereco || business.bairro || municipio) {
    schema.address = {
      '@type': 'PostalAddress',
      addressCountry: 'BR',
    };
    if (!business.ocultar_endereco && business.endereco) {
      schema.address.streetAddress = business.endereco;
    }
    if (business.bairro) schema.address.addressLocality = business.bairro;
    if (municipio?.nome) schema.address.addressRegion = municipio.nome;
    if (municipio?.uf) schema.address.addressRegion = `${municipio.uf}`;
  }

  // Geo
  if (business.latitude && business.longitude) {
    schema.geo = {
      '@type': 'GeoCoordinates',
      latitude: business.latitude,
      longitude: business.longitude,
    };
  }

  // Rating
  if (business.rating_avg && business.rating_count) {
    schema.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: business.rating_avg,
      reviewCount: business.rating_count,
    };
  }

  // Opening hours
  if (business.horarios && Array.isArray(business.horarios)) {
    const dayMap = {
      segunda: 'Monday', terca: 'Tuesday', quarta: 'Wednesday',
      quinta: 'Thursday', sexta: 'Friday', sabado: 'Saturday', domingo: 'Sunday'
    };
    const specs = business.horarios
      .filter(h => !h.fechado && h.abre && h.fecha)
      .map(h => ({
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: dayMap[h.dia] || h.dia,
        opens: h.abre,
        closes: h.fecha,
      }));
    if (specs.length > 0) schema.openingHoursSpecification = specs;
  }

  // Social links
  const sameAs = [];
  if (business.instagram) {
    const handle = business.instagram.replace('@', '');
    sameAs.push(`https://www.instagram.com/${handle}`);
  }
  if (business.site) sameAs.push(business.site);
  if (sameAs.length > 0) schema.sameAs = sameAs;

  return schema;
}

/**
 * Build BreadcrumbList JSON-LD
 * @param {Array} items - [{name: 'Home', url: '/'}, {name: 'Ponte Nova', url: '/ponte-nova'}, ...]
 */
export function buildBreadcrumbJsonLd(items) {
  const baseUrl = window.location.origin;
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url ? `${baseUrl}${item.url}` : undefined,
    })),
  };
}

/**
 * Build programmatic title for category pages
 */
export function buildCategoryTitle(categoryName, subcategoryName, municipioName, uf) {
  if (subcategoryName) {
    return `${subcategoryName} em ${municipioName}, ${uf}`;
  }
  return `${categoryName} em ${municipioName}, ${uf}`;
}

/**
 * Build programmatic description for category pages
 */
export function buildCategoryDescription(categoryName, subcategoryName, municipioName, uf, count) {
  const subject = subcategoryName || categoryName;
  if (count > 0) {
    return `Encontre ${count} ${subject.toLowerCase()} em ${municipioName}, ${uf}. Veja avaliações, contatos e localizações no Empreende Comunidade.`;
  }
  return `Encontre ${subject.toLowerCase()} em ${municipioName}, ${uf}. Descubra os melhores profissionais e negócios locais no Empreende Comunidade.`;
}
